import React from 'react';
import { HeaderSkeleton } from './HeaderSkeleton';
import { SearchBarAndFiltersSkeleton } from './SearchBarAndFiltersSkeleton';
import { DataTableSkeleton } from './DataTableSkeleton';
import { PaginationSkeleton } from './PaginationSkeleton';

interface GradeStudentsSkeletonProps {
  /**
   * Number of rows to show in the skeleton table
   */
  rowCount?: number;

  /**
   * Additional class names
   */
  className?: string;
}

/**
 * GradeStudentsSkeleton component
 *
 * This component provides a loading skeleton specifically designed for the grade students page.
 * It includes a header, tabs, search bar, and a table with customizable number of rows.
 *
 * @example
 * <GradeStudentsSkeleton />
 */
export const GradeStudentsSkeleton: React.FC<GradeStudentsSkeletonProps> = ({
  rowCount = 5,
  className = '',
}) => {
  return (
    <div className={`min-h-screen bg-[#F5F8F9] ${className}`}>
      <div className="max-w-[1600px] mx-auto p-6 pb-24 space-y-8">
        {/* Grade Header Skeleton */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 animate-pulse">
          <div>
            <div className="h-8 w-48 bg-gray-200 rounded-md"></div>
            <div className="h-5 w-32 bg-gray-100 rounded-md mt-2"></div>
          </div>
          <div className="flex gap-3">
            <div className="h-10 w-28 bg-gray-200 rounded-md"></div>
            <div className="h-10 w-36 bg-gray-200 rounded-md"></div>
          </div>
        </div>

        {/* Tabs Navigation Skeleton */}
        <div className="border-b border-gray-200 mt-8">
          <div className="flex space-x-8">
            <div className="h-10 w-20 bg-gray-200 rounded-md border-b-2 border-primary"></div>
            <div className="h-10 w-24 bg-gray-200 rounded-md"></div>
          </div>
        </div>

        {/* Search Bar and Filters Skeleton */}
        <div className="mt-8">
          <SearchBarAndFiltersSkeleton filterCount={2} />
        </div>

        {/* Data Table Skeleton */}
        <div className="mt-8">
          <DataTableSkeleton
            rowCount={rowCount}
            columnCount={8}
            withCheckbox={true}
            withActions={true}
          />
        </div>

        {/* Pagination Skeleton */}
        <div className="mt-8">
          <PaginationSkeleton />
        </div>
      </div>
    </div>
  );
};

/**
 * GradeAttendanceTabSkeleton component
 *
 * This component provides a loading skeleton specifically for the attendance tab
 * in the grade students page.
 *
 * @example
 * <GradeAttendanceTabSkeleton />
 */
export const GradeStudentsAttendanceSkeleton: React.FC<{
  className?: string;
}> = ({ className = '' }) => {
  return (
    <div className={`${className}`}>
      {/* Section Selector Skeleton */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-[#E4E8EA] p-4 mt-8 animate-pulse">
        <div className="flex items-center gap-4">
          <div className="h-10 w-64 bg-gray-200 rounded-md"></div>
        </div>
      </div>

      {/* Calendar Header Skeleton */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E4E8EA] p-4 animate-pulse">
        <div className="flex justify-between items-center mb-4">
          <div className="h-6 w-32 bg-gray-200 rounded-md"></div>
          <div className="flex gap-2">
            <div className="h-8 w-8 bg-gray-200 rounded-md"></div>
            <div className="h-8 w-8 bg-gray-200 rounded-md"></div>
          </div>
        </div>

        {/* Calendar Grid Skeleton */}
        <div className="grid grid-cols-7 gap-1">
          {/* Day headers */}
          {[...Array(7)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-100 rounded-md"></div>
          ))}

          {/* Calendar cells - 5 weeks */}
          {[...Array(35)].map((_, i) => (
            <div
              key={i}
              className="h-16 bg-gray-50 rounded-md p-1"
              style={{ animationDelay: `${i * 0.01}s` }}
            >
              <div className="h-4 w-4 bg-gray-200 rounded-sm mb-2"></div>
              <div className="h-3 w-full bg-gray-100 rounded-sm"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
